import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  createResourceLocatorString,
  findRouteByRouteName,
} from '../../util/routes';
import { formatMoney } from '../../util/currency';
import { timestampToDate, logBookingDates, getCalendarDateISO } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { orderImagesByCoverPhoto } from '../../util/listingImages';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';
import { initiatePrivileged, updateMetaData, saveBookingData } from '../../util/api';
import { transitions } from '../../transactions/transactionProcessBooking';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

// Sharetribe only allows a single transaction's booking to span up to 365
// days. Bookings selected from the (up to 24-month) availability calendar
// that exceed this must be split into two linked Sharetribe transactions,
// invisibly to the user.
const MAX_SHARETRIBE_BOOKING_DAYS = 365;

// Renders a calendar date stored as UTC midnight (see getCalendarDateISO) as a
// human readable label, e.g. 'January 1, 2027'. Formatted in UTC so the label
// always names the day the guest actually picked.
const formatCalendarDateLabel = isoDate =>
  isoDate
    ? new Date(isoDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : null;

// Weekday of the same calendar date ('Friday'), so the emails can keep their
// two-line "weekday over date" layout without having to derive the weekday
// from a protectedData string themselves.
const formatCalendarDayLabel = isoDate =>
  isoDate
    ? new Date(isoDate).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
    : null;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

// Sharetribe keeps holding the first segment's end day *and* the day after it,
// so the second segment has to start two days later - a 1-day gap was tried and
// still failed with "transaction-booking-time-not-available" (409).
//
// Those two gap days are paid for but belong to neither transaction; they are
// only blocked on the calendar via `occupiedEnd`, so nobody else can book them.
const SEGMENT_GAP_DAYS = 2;

// If Sharetribe still reports the second segment's start as taken, its start is
// pushed one more day and retried this many times before giving up.
const MAX_SEGMENT_START_SHIFTS = 5;

// Whether a booking longer than 365 days is created as two linked Sharetribe
// transactions (a "parent" and a "child"). Turned off for now: such a booking
// becomes a single transaction for its first 365 days, because the second
// transaction kept being rejected with "booking time not available".
const CREATE_SPLIT_BOOKING_TRANSACTIONS = false;

// True for the Marketplace API's "these dates are already taken" rejection
// (HTTP 409), which is the one failure a later start date can fix.
const isBookingTimeNotAvailableError = error => {
  const errors = error?.data?.errors || error?.errors || [];
  const codes = errors.map(err => err?.code);
  return (
    error?.status === 409 ||
    codes.includes('transaction-booking-time-not-available') ||
    codes.includes('booking-time-not-available')
  );
};

// booking_data rows record the *inclusive* last occupied day - that is how
// the booking calendar reads them back (it adds the exclusive day itself).
// A daily booking's `bookingEnd` is already exclusive (the picker turns a
// 31 Dec checkout into 1 Jan before the form ever sees it), so it has to be
// shifted a day back before it is stored, or a 1 Dec - 31 Dec stay would be
// saved as ending 1 Jan and block that day for the next guest.
const occupiedEndDateISO = (bookingEnd, timeZone, isExclusiveEnd) => {
  const endISO = getCalendarDateISO(bookingEnd, timeZone);
  return isExclusiveEnd
    ? new Date(new Date(endISO).getTime() - DAY_IN_MS).toISOString()
    : endISO;
};

const splitBookingRange = (bookingStart, bookingEnd) => {
  const start = new Date(bookingStart);
  const end = new Date(bookingEnd);
  const totalDays = Math.round((end.getTime() - start.getTime()) / DAY_IN_MS);

  if (totalDays <= MAX_SHARETRIBE_BOOKING_DAYS) {
    return [{ bookingStart: start, bookingEnd: end }];
  }

  // The first segment takes as many days as Sharetribe allows, but never so many
  // that the gap swallows the rest of the range: at a total of 366 or 367 days a
  // full 365-day first segment leaves a second segment of zero or negative
  // length, which the API rejects ("transition should contain quantity
  // information" / "End date cannot be before start date"). Always leave at
  // least one day for the second segment.
  const firstSegmentDays = Math.min(
    MAX_SHARETRIBE_BOOKING_DAYS,
    totalDays - SEGMENT_GAP_DAYS - 1
  );

  if (firstSegmentDays < 1) {
    // Too short to split with a gap at all - send it as a single segment rather
    // than a degenerate range.
    return [{ bookingStart: start, bookingEnd: end }];
  }

  const firstSegmentEnd = new Date(start.getTime() + firstSegmentDays * DAY_IN_MS);
  const secondSegmentStart = new Date(firstSegmentEnd.getTime() + SEGMENT_GAP_DAYS * DAY_IN_MS);

  return [
    { bookingStart: start, bookingEnd: firstSegmentEnd },
    { bookingStart: secondSegmentStart, bookingEnd: end },
  ];
};

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  orderImagesByCoverPhoto(listing.images || [], listing.attributes?.publicData)
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    const state = {
      from: `${location.pathname}${location.search}${location.hash}`,
    };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, {
      inquiryModalOpenForListingId: params.id,
    });

    // signup and return back to listingPage.
    history.push(
      createResourceLocatorString('SignupPage', routes, {}, {}),
      state
    );
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => values => {
  const {
    history,
    params,
    getListing,
    onSendInquiry,
    routes,
    setInquiryModalOpen,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  onSendInquiry(listing, message.trim())
    .then(txId => {
      setInquiryModalOpen(false);
      const metaData = {
        isproviderInquiry: 'unread',
      };
      updateMetaData({ transactionId: txId, metadata: { metaData } });
      // Redirect to OrderDetailsPage
      history.push(
        createResourceLocatorString(
          'OrderDetailsPage',
          routes,
          { id: txId.uuid },
          {}
        )
      );
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
    onSendMessage,
    onTransition,
  } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const {
    bookingDates,
    guests,
    pets,
    // Prices as shown to the guest in the booking form - saved with the
    // transaction so the transaction page shows the same numbers.
    bookingPrices,
    additionalMessage,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    quantity: quantityRaw,
    deliveryMethod,
    ...otherOrderData
  } = values;
  const bookingMaybe = bookingDates
    ? {
        bookingDates: {
          bookingStart: bookingDates.startDate,
          bookingEnd: bookingDates.endDate,
        },
      }
    : bookingStartTime && bookingEndTime
    ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
    : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  const initialValues = {
    listing,
    orderData: {
      quantity: 1,
      ...bookingMaybe,
      ...otherOrderData,
    },
  };

  const saveToSessionStorage = !currentUser;

  // Customize checkout page state with current listing and selected orderData
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
  const statusChangedAt = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  });
  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);
  const bodyParams = {
    transition: 'transition/request-payment',
    processAlias: listing.attributes.publicData.transactionProcessAlias,
    params: {
      listingId: listing.id.uuid,
      protectedData: {
        guests,
        pets,
        statusChangedAt,
        isParentTransaction: true
      },
      ...bookingMaybe.bookingDates,
    },
  };

  const queryParams = {
    include: ['booking', 'customer'],
    expand: true,
  };

  if (bookingMaybe.bookingDates) {
    logBookingDates(
      'ListingPage handleSubmit - full selected range',
      bookingMaybe.bookingDates.bookingStart,
      bookingMaybe.bookingDates.bookingEnd
    );
  }

  // Booking ranges within the 365-day Sharetribe limit create exactly one
  // segment; longer ranges (up to the 24-month availability window) are
  // split into two contiguous segments here, transparently to the user.
  const allBookingSegments = bookingMaybe.bookingDates
    ? splitBookingRange(
        bookingMaybe.bookingDates.bookingStart,
        bookingMaybe.bookingDates.bookingEnd
      )
    : [null];

  // The second, linked transaction is switched off: a >365-day selection now
  // becomes a single transaction covering only its first (<=365-day) segment.
  // The guest's full selection is still saved in protectedData
  // (fullBookingStartDate/fullBookingEndDate) and is what the UI and the
  // notification emails show. Set CREATE_SPLIT_BOOKING_TRANSACTIONS back to
  // true to restore the two-transaction flow - everything it needs
  // (splitBookingRange, the *-child transitions, the parent/child linking and
  // the propagation on the transaction page) is still in place.
  const bookingSegments = CREATE_SPLIT_BOOKING_TRANSACTIONS
    ? allBookingSegments
    : allBookingSegments.slice(0, 1);

  bookingSegments.forEach((segment, index) => {
    if (segment) {
      logBookingDates(
        `ListingPage handleSubmit - segment ${index + 1}/${bookingSegments.length} sent to API`,
        segment.bookingStart,
        segment.bookingEnd
      );
    }
  });

  // Creates one Sharetribe transaction for a single booking segment, then
  // records it in booking_data. `parentTransactionId` links a second
  // ("child") segment back to the first ("parent") one. The parent is
  // additionally flagged with `isParentTransaction: true`, and both halves
  // carry the user's full originally-selected date range (which can span up
  // to 24 months) since each transaction's own `booking.start/end` only ever
  // reflects its own <=365-day segment.
  const createTransactionSegment = (segment, parentTransactionId, occupiedEnd) => {
    const linkedDataMaybe = parentTransactionId
      ? { parentTransactionId: parentTransactionId.uuid }
      : { isParentTransaction: true };
    // Store the selected calendar days (as seen in the listing's time zone)
    // pinned to UTC midnight, so the saved value reads as the day the guest
    // actually picked no matter which time zone it's read or rendered in.
    const listingTimeZone = listing?.attributes?.availabilityPlan?.timezone;
    // Only daily listings get the exclusive end-date shift from the picker;
    // a nightly booking's end date is the checkout day as picked.
    const hasExclusiveBookingEnd = listing?.attributes?.publicData?.unitType === 'day';
    const fullBookingStartISO = bookingMaybe.bookingDates
      ? getCalendarDateISO(bookingMaybe.bookingDates.bookingStart, listingTimeZone)
      : null;
    // Saved as the last day of the stay (31 Dec for a 1 Dec - 31 Dec booking),
    // matching what booking_data stores, rather than the exclusive end the
    // picker produces for daily listings.
    const fullBookingEndISO = bookingMaybe.bookingDates
      ? occupiedEndDateISO(
          bookingMaybe.bookingDates.bookingEnd,
          listingTimeZone,
          hasExclusiveBookingEnd
        )
      : null;
    // Ready-made labels for the notification email templates: each Sharetribe
    // transaction's own booking only covers its <=365-day segment, and the
    // templates can't format a protectedData date string themselves.
    const fullBookingDatesMaybe = bookingMaybe.bookingDates
      ? {
          fullBookingStartDate: fullBookingStartISO,
          fullBookingEndDate: fullBookingEndISO,
          fullBookingStartLabel: formatCalendarDateLabel(fullBookingStartISO),
          fullBookingEndLabel: formatCalendarDateLabel(fullBookingEndISO),
          fullBookingStartDayLabel: formatCalendarDayLabel(fullBookingStartISO),
          fullBookingEndDayLabel: formatCalendarDayLabel(fullBookingEndISO),
        }
      : {};

    // A booking longer than 365 days is created as a single, shorter
    // Sharetribe transaction, so its own payinTotal doesn't cover the full
    // stay. Save the totals the guest saw instead of letting the transaction
    // page recompute them from the segment.
    const bookingPricesMaybe = bookingPrices ? { bookingPrices } : {};

    // How long Sharetribe keeps holding the previous segment's dates isn't
    // something we can know up front, so a follow-up segment that comes back
    // with "booking time not available" is retried with its start pushed one
    // day further, instead of failing the whole booking.
    const initiateSegment = (currentSegment, attempt = 0) => {
      const bodyParams = {
        transition: 'transition/request-payment',
        processAlias: listing.attributes.publicData.transactionProcessAlias,
        params: {
          listingId: listing.id.uuid,
          protectedData: {
            guests,
            pets,
            statusChangedAt,
            ...linkedDataMaybe,
            ...fullBookingDatesMaybe,
            ...bookingPricesMaybe,
          },
          ...(currentSegment || {}),
        },
      };

      const orderData = {
        ...(currentSegment || {}),
        guests,
        pets,
      };

      return initiatePrivileged({
        isSpeculative: false,
        orderData,
        bodyParams,
        queryParams,
      }).catch(e => {
        const canRetry =
          parentTransactionId &&
          currentSegment &&
          attempt < MAX_SEGMENT_START_SHIFTS &&
          isBookingTimeNotAvailableError(e);

        if (!canRetry) {
          throw e;
        }

        const shiftedStart = new Date(currentSegment.bookingStart.getTime() + DAY_IN_MS);
        if (shiftedStart.getTime() >= currentSegment.bookingEnd.getTime()) {
          throw e;
        }

        const shiftedSegment = { ...currentSegment, bookingStart: shiftedStart };
        logBookingDates(
          `ListingPage handleSubmit - segment start taken, retrying one day later (attempt ${attempt +
            1}/${MAX_SEGMENT_START_SHIFTS})`,
          shiftedSegment.bookingStart,
          shiftedSegment.bookingEnd
        );

        return initiateSegment(shiftedSegment, attempt + 1);
      });
    };

    return initiateSegment(segment).then(res => {
      const txId = res?.data?.data?.id;

      // Record the booking in our own DB as soon as the transaction exists.
      // Sharetribe reserves the dates already at request-payment, so anything
      // that fails after this point (confirm-payment, a later segment, the user
      // closing the tab) still leaves those dates occupied. Recording only once
      // confirm-payment had succeeded left such reservations invisible to the
      // booking calendar, which then let a guest select a range crossing them
      // and hit a 409 "booking time not available" on submit.
      //
      // `occupiedEnd` covers the gap days between two split segments -
      // Sharetribe holds those too, so they have to be blocked as well.
      // Side effect only - it must never break the "request to book" flow.
      const recordBooking = segment
        ? saveBookingData({
          listingId: listing.id.uuid,
          transactionId: txId.uuid,
          parentTransactionId: parentTransactionId ? parentTransactionId.uuid : null,
          // The form hands over start-of-day *in the listing's time zone*, so
          // for a zone ahead of UTC that instant still falls on the previous
          // UTC day (1 Dec 00:00 +05:30 = 30 Nov 18:30 UTC). The server keeps
          // only the date part of what it receives, so sending the raw instant
          // stored the booking one day early and blocked the wrong day in the
          // calendar. Pin the picked calendar day to UTC midnight instead.
          bookingStartDate: getCalendarDateISO(segment.bookingStart, listingTimeZone),
          bookingEndDate: occupiedEndDateISO(
            occupiedEnd || segment.bookingEnd,
            listingTimeZone,
            hasExclusiveBookingEnd
          ),
          bookingStatus: 'pending',
        }).catch(e => {
          console.error('save-booking-data-failed', e);
        })
        : Promise.resolve();

      // Wait for confirm-payment to actually finish before resolving. The
      // app tracks "transition in progress" as a single global flag, so
      // firing a second segment's confirm-payment before this one finishes
      // fails with "Transition already in progress" and silently leaves
      // that segment stuck unconfirmed.
      return recordBooking
        // The child segment confirms with the notification-free variant, so the
        // provider gets a single "new booking request" email for the booking,
        // not one per segment.
        .then(() =>
          onTransition(
            txId,
            parentTransactionId ? transitions.CONFIRM_PAYMENT_CHILD : transitions.CONFIRM_PAYMENT,
            {}
          )
        )
        .then(() => {
        const metaData = {
          isprovider: 'unread',
        };
        updateMetaData({ transactionId: txId, metadata: { metaData } });

        // This segment is a child (split booking > 365 days) - also record
        // its id back on the parent transaction's metadata, so the parent
        // can be used to look up the child the same way the child already
        // looks up the parent via protectedData.parentTransactionId.
        if (parentTransactionId) {
          updateMetaData({
            transactionId: parentTransactionId,
            metadata: { metaData: { isprovider: 'unread', childTransactionId: txId.uuid } },
          }).catch(e => {
            console.error('save-child-transaction-id-on-parent-failed', e);
          });
        }

        return txId;
      });
    });
  };

  // Create the first ("parent") transaction, then - only if the booking had
  // to be split - the second ("child") one linked to it. The user only ever
  // sees/interacts with the parent transaction; the child is fully handled
  // here in the background.
  // A segment followed by another one also holds the gap days in between, so it
  // must block up to the next segment's start rather than its own end.
  // With the split disabled there is no next segment to hand over to, so the
  // single transaction blocks the guest's whole selected range instead of just
  // its own <=365-day booking - those dates are spoken for either way.
  const occupiedEndFor = index =>
    CREATE_SPLIT_BOOKING_TRANSACTIONS
      ? bookingSegments[index + 1]?.bookingStart || null
      : bookingMaybe.bookingDates?.bookingEnd || null;

  return createTransactionSegment(bookingSegments[0], null, occupiedEndFor(0)).then(primaryTxId => {
    const remainingSegments = bookingSegments.slice(1);

    const createRemainingSegments = remainingSegments.reduce(
      (promise, segment, index) =>
        promise.then(() =>
          createTransactionSegment(segment, primaryTxId, occupiedEndFor(index + 1))
        ),
      Promise.resolve()
    );

    return createRemainingSegments.then(() => {
      if (additionalMessage) {
        onSendMessage({ message: additionalMessage, id: primaryTxId });
      }

      onInitializeCardPaymentData();
      history.push(
        createResourceLocatorString(
          'OrderDetailsPage',
          routes,
          { id: primaryTxId.uuid },
          {}
        )
      );
    });
  });

  // Clear previous Stripe errors from store if there is any

  // Redirect to CheckoutPage
  // history.push(
  //   createResourceLocatorString(
  //     'CheckoutPage',
  //     routes,
  //     { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
  //     {}
  //   )
  // );
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, invalidListing, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        {invalidListing ? (
          <FormattedMessage id="ListingPage.errorInvalidListingMessage" />
        ) : (
          <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
        )}
      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingListingMessage" />
      </p>
    </PlainPage>
  );
};
